/**
 * @file Lifecycle + mutation actions for `mappingReviewState` (the repo
 * precedent of `extractionMappingEditorState`: named action exports mutating
 * a module-scoped proxy; no effects — the one async boundary, the initial
 * Storage load, is kicked from `openMappingReview` itself and installs under
 * a toolCallId stale-guard). The session edits a live copy of the staged
 * proposal; the persisted `ProcessUserDecisions` overlay is DERIVED at save
 * time by `buildEditedProcessDocument` as a diff against the untouched
 * `baseDoc`, so model judgments and user decisions never mix. A
 * whole-document proposal edits header fields, references, and one table
 * slice per `TablePlan`; an entity import edits a single table slice. Re-
 * opening a saved doc re-applies its persisted decisions onto fresh live
 * state, so the round-trip is lossless.
 */
import { ref } from 'valtio';
import { buildFileFolder, CONFIDENCE_WARN_THRESHOLD } from '#types';
import type {
  AgenticApprovalRequiredEvent,
  CellCleaningRule,
  Chunk,
  ColumnAlternative,
  ColumnPlan,
  DateLocale,
  DocumentMappingSummary,
  ExtractedFact,
  FieldPlan,
  LocaleProvenance,
  NumberLocale,
  ProcessColumnDecision,
  ProcessDocument,
  ProcessDocumentDecisions,
  ProcessFieldDecision,
  ProcessImportDecisions,
  ProcessImportDocument,
  ProcessReferenceDecision,
  ProcessTableDecisions,
  ProcessWholeDocument,
  ReferencePlan,
  TableChunk,
  TableMappingSummary,
  TablePlan,
  TableReferenceValueResolution,
} from '#types';
import { loadMappingReviewSource, resolveTableChunks } from '@/storage/loadMappingReviewSource';
import { referenceKeyOccurrences } from './referenceKeyOccurrences';
import { mappingReviewState } from './mappingReviewState';
import type {
  MappingReviewFieldLive,
  MappingReviewReferenceLive,
  MappingReviewSession,
  MappingReviewTableSlice,
} from './mappingReviewState';

function session(): MappingReviewSession | null {
  return mappingReviewState.session;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isWholeDocument(doc: ProcessDocument): doc is ProcessWholeDocument {
  return doc.target.kind === 'document';
}

/** The live slice a row/column mutator targets: the import slice or a named document table. */
function tableSliceFor(groupId: string): MappingReviewTableSlice | null {
  const s = session();
  if (!s) return null;
  if (s.import && s.import.groupId === groupId) return s.import;
  return s.tables[groupId] ?? null;
}

// ---------------------------------------------------------------------------
// Slice construction + decisions hydration
// ---------------------------------------------------------------------------

/**
 * Applies a persisted `TableMappingDecisions` overlay onto a fresh copy of the
 * proposed columns — the reopen-after-reload path. Mirrors the server's
 * "set" semantics: a retargeted column drops the model's resolution before
 * user overrides merge on top.
 */
function columnsWithDecisions(
  baseColumns: ColumnPlan[],
  decisions: ProcessTableDecisions | ProcessImportDecisions | undefined,
): ColumnPlan[] {
  const live = clone(baseColumns);
  if (!decisions) return live;
  const result = live.filter((c) => decisions.columns[c.sourceColumn]?.kind !== 'remove');
  for (const [sourceColumn, decision] of Object.entries(decisions.columns)) {
    if (decision.kind !== 'set') continue;
    const existing = result.find((c) => c.sourceColumn === sourceColumn);
    if (existing) {
      if (decision.targetField !== existing.targetField) delete existing.resolution;
      existing.targetField = decision.targetField;
      if (decision.rules) existing.rules = decision.rules;
      if (decision.percentScale !== undefined) existing.percentScale = decision.percentScale;
      if (decision.resolutionOverrides) {
        existing.resolution = { ...existing.resolution, ...decision.resolutionOverrides };
      }
    } else {
      result.push({
        sourceColumn,
        targetField: decision.targetField,
        confidence: 1,
        alternatives: [],
        rules: decision.rules ?? [],
        ...(decision.percentScale !== undefined ? { percentScale: decision.percentScale } : {}),
        ...(decision.resolutionOverrides ? { resolution: decision.resolutionOverrides } : {}),
      });
    }
  }
  return result;
}

/**
 * Enforces the one-plan-per-source-column invariant on the model's proposal: a
 * row record holds a single value per source column, so two plans for the same
 * `sourceColumn` are contradictory and break React keying + every column
 * mutator. Keeps the highest-confidence plan per source column (ties → the
 * earlier entry) in first-occurrence order.
 */
function dedupeColumns(columns: ColumnPlan[]): ColumnPlan[] {
  const bySource = new Map<string, ColumnPlan>();
  for (const column of columns) {
    const kept = bySource.get(column.sourceColumn);
    if (!kept || column.confidence > kept.confidence) bySource.set(column.sourceColumn, column);
  }
  return [...bySource.values()];
}

/**
 * Builds one editable table slice from a plan's axes (`baseColumns`,
 * `baseRows`, the resolved `chunks`) and an optional persisted overlay. The
 * import slice and each document table share this shape. `targetTableId`
 * defaults to the plan's table; a persisted `target.set` retargets it, a
 * `target.remove` excludes the group.
 */
function buildSlice(args: {
  groupId: string;
  chunks: TableChunk[];
  baseColumns: ColumnPlan[];
  unmappedTargets: Record<string, ColumnAlternative[]>;
  baseRows: TablePlan['rows'];
  planTableId: string;
  decisions: ProcessTableDecisions | ProcessImportDecisions | undefined;
  locale?: NumberLocale;
  dateLocale?: DateLocale;
  centuryPivot?: number;
  dateZone?: string;
}): MappingReviewTableSlice {
  const { groupId, chunks, baseColumns, unmappedTargets, baseRows, planTableId, decisions, locale, dateLocale, centuryPivot, dateZone } = args;
  const dedupedColumns = dedupeColumns(baseColumns);
  const tableDecision = decisions && 'target' in decisions ? decisions.target : undefined;
  const rejectedRows: Record<number, true> = {};
  for (const index of decisions?.rejectedRows ?? []) rejectedRows[index] = true;
  return {
    groupId,
    chunks: ref(chunks),
    baseColumns: ref(dedupedColumns),
    unmappedTargets: ref(unmappedTargets),
    baseRows: ref(baseRows),
    columns: columnsWithDecisions(dedupedColumns, decisions),
    rejectedRows,
    cellOverrides: decisions ? clone(decisions.cellOverrides) : {},
    selection: { selected: {}, count: 0, lastClicked: null },
    activeChunkId: null,
    targetTableId: tableDecision?.kind === 'set' ? tableDecision.tableId : planTableId,
    targetRemoved: tableDecision?.kind === 'remove',
    ...(locale ? { locale } : {}),
    ...(dateLocale ? { dateLocale } : {}),
    ...(centuryPivot !== undefined ? { centuryPivot } : {}),
    ...(dateZone ? { dateZone } : {}),
  };
}

/** Live field state from a `FieldPlan` and an optional persisted decision. */
function buildFieldLive(
  plan: FieldPlan,
  facts: ExtractedFact[],
  decision: ProcessFieldDecision | undefined,
): MappingReviewFieldLive {
  const proposed = facts[plan.factIndex]?.value ?? null;
  if (decision?.kind === 'remove') {
    return { targetField: plan.targetField, factIndex: plan.factIndex, value: proposed, removed: true, manual: false };
  }
  if (decision?.kind === 'set') {
    return { targetField: plan.targetField, factIndex: plan.factIndex, value: decision.value, removed: false, manual: true };
  }
  return { targetField: plan.targetField, factIndex: plan.factIndex, value: proposed, removed: false, manual: false };
}

/** Live reference state from a `ReferencePlan` and an optional persisted decision. */
function buildReferenceLive(
  plan: ReferencePlan,
  decision: ProcessReferenceDecision | undefined,
): MappingReviewReferenceLive {
  const common = {
    slotKey: plan.slotKey,
    entityId: plan.entityId,
    ...(plan.entityGroupId ? { entityGroupId: plan.entityGroupId } : {}),
    factIndex: plan.factIndex,
  };
  if (decision?.kind === 'remove') {
    return { ...common, resolution: clone(plan.resolution), removed: true, manual: false };
  }
  if (decision?.kind === 'set') {
    return { ...common, resolution: clone(decision.resolution), removed: false, manual: true };
  }
  return { ...common, resolution: clone(plan.resolution), removed: false, manual: false };
}

/**
 * The fully-built slices for one staged proposal, assembled into locals before
 * any proxy mutation so a mid-build throw (an unknown selection or a non-table
 * chunk in `resolveTableChunks`) never half-hydrates the session.
 * `loadStagedSource` commits them to the proxy atomically with `baseDoc` last,
 * so an un-hydrated session always has `baseDoc === null` — the contract
 * Approve gates on.
 */
function buildStagedSlices(doc: ProcessDocument, chunks: Chunk[]): {
  fields: Record<string, MappingReviewFieldLive>;
  references: Record<string, MappingReviewReferenceLive>;
  tables: Record<string, MappingReviewTableSlice>;
  tableOrder: string[];
  import: MappingReviewTableSlice | null;
} {
  const fields: Record<string, MappingReviewFieldLive> = {};
  const references: Record<string, MappingReviewReferenceLive> = {};
  const tables: Record<string, MappingReviewTableSlice> = {};
  const tableOrder: string[] = [];
  if (!isWholeDocument(doc)) {
    return {
      fields,
      references,
      tables,
      tableOrder,
      import: buildSlice({
        groupId: doc.source.ref.id,
        chunks: resolveTableChunks(chunks, doc.source.ref),
        baseColumns: doc.columns,
        unmappedTargets: doc.unmappedTargets,
        baseRows: doc.rows,
        planTableId: doc.target.entityId,
        decisions: doc.decisions,
        ...(doc.locale ? { locale: doc.locale } : {}),
        ...(doc.dateLocale ? { dateLocale: doc.dateLocale } : {}),
        ...(doc.centuryPivot !== undefined ? { centuryPivot: doc.centuryPivot } : {}),
        ...(doc.dateZone ? { dateZone: doc.dateZone } : {}),
      }),
    };
  }
  for (const plan of doc.fields) {
    fields[plan.targetField] = buildFieldLive(plan, doc.facts, doc.decisions?.fields[plan.targetField]);
  }
  const proposedSlots = new Set(doc.references.map((p) => p.slotKey));
  for (const plan of doc.references) {
    references[plan.slotKey] = buildReferenceLive(plan, doc.decisions?.references[plan.slotKey]);
  }
  for (const [slotKey, decision] of Object.entries(doc.decisions?.references ?? {})) {
    if (decision.kind !== 'set' || proposedSlots.has(slotKey)) continue;
    references[slotKey] = {
      slotKey,
      entityId: decision.entityId,
      ...(decision.entityGroupId ? { entityGroupId: decision.entityGroupId } : {}),
      factIndex: -1,
      resolution: clone(decision.resolution),
      removed: false,
      manual: true,
    };
  }
  for (const plan of doc.tables) {
    const groupId = plan.ref.id;
    if (tables[groupId]) continue;
    tables[groupId] = buildSlice({
      groupId,
      chunks: resolveTableChunks(chunks, plan.ref),
      baseColumns: plan.columns,
      unmappedTargets: plan.unmappedTargets,
      baseRows: plan.rows,
      planTableId: plan.tableId,
      decisions: doc.decisions?.tables[groupId],
      ...(doc.locale ? { locale: doc.locale } : {}),
      ...(doc.dateLocale ? { dateLocale: doc.dateLocale } : {}),
      ...(doc.centuryPivot !== undefined ? { centuryPivot: doc.centuryPivot } : {}),
      ...(doc.dateZone ? { dateZone: doc.dateZone } : {}),
    });
    tableOrder.push(groupId);
  }
  return { fields, references, tables, tableOrder, import: null };
}

async function loadStagedSource(
  toolCallId: string,
  fileFolder: string,
  processIndex: number,
): Promise<void> {
  try {
    const { chunks, doc } = await loadMappingReviewSource(fileFolder, processIndex);
    const built = buildStagedSlices(doc, chunks);
    const s = session();
    if (!s || s.approval.toolCallId !== toolCallId) return;
    s.target = doc.target;
    if (doc.locale) s.locale = doc.locale;
    if (doc.dateLocale) s.dateLocale = doc.dateLocale;
    if (doc.centuryPivot !== undefined) s.centuryPivot = doc.centuryPivot;
    if (doc.dateZone) s.dateZone = doc.dateZone;
    if (doc.localeSource) s.localeSource = { ...doc.localeSource };
    if (doc.encoding) s.encoding = { ...doc.encoding };
    Object.assign(s.fields, built.fields);
    Object.assign(s.references, built.references);
    Object.assign(s.tables, built.tables);
    s.tableOrder.push(...built.tableOrder);
    s.import = built.import;
    s.baseDoc = ref(doc);
    s.loading = false;
  } catch (err) {
    console.error(`Failed to load mapping review source (toolCallId=${toolCallId}, fileFolder=${fileFolder}, processIndex=${processIndex}):`, err);
    const s = session();
    if (!s || s.approval.toolCallId !== toolCallId) return;
    s.loadError = err instanceof Error ? err.message : 'Failed to load mapping review source';
    s.loading = false;
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export function openMappingReview(event: AgenticApprovalRequiredEvent, uid: string): void {
  const review = event.review;
  if (!review) return;
  if (mappingReviewState.session?.approval.toolCallId === event.toolCallId) {
    mappingReviewState.panel.open = true;
    return;
  }
  const fileFolder = buildFileFolder(uid, review.fileId);
  mappingReviewState.session = {
    approval: event,
    fileFolder,
    processIndex: review.processIndex,
    target: null,
    baseDoc: null,
    import: null,
    fields: {},
    references: {},
    tables: {},
    tableOrder: [],
    dirty: false,
    loading: true,
    loadError: null,
    saving: false,
  };
  mappingReviewState.panel.open = true;
  void loadStagedSource(event.toolCallId, fileFolder, review.processIndex);
}

export function closeMappingReview(): void {
  mappingReviewState.session = null;
  mappingReviewState.panel.open = false;
  mappingReviewState.panel.expanded = false;
}

/**
 * DEV ONLY — seeds a fully-hydrated session from an in-memory `{ doc, chunks }`
 * pair, bypassing the Storage load so `MappingReviewDevMenu` can drive the
 * panel against fabricated data shapes. Runs the same `buildStagedSlices`
 * hydration a live approval uses, so the rendered surface matches production.
 * Gated by `devFlag` at the call site.
 */
export function seedMappingReviewSource(
  approval: AgenticApprovalRequiredEvent,
  fileFolder: string,
  doc: ProcessDocument,
  chunks: Chunk[],
): void {
  const built = buildStagedSlices(doc, chunks);
  mappingReviewState.session = {
    approval,
    fileFolder,
    processIndex: approval.review?.processIndex ?? 0,
    target: doc.target,
    baseDoc: ref(doc),
    import: built.import,
    fields: built.fields,
    references: built.references,
    tables: built.tables,
    tableOrder: built.tableOrder,
    ...(doc.locale ? { locale: doc.locale } : {}),
    ...(doc.dateLocale ? { dateLocale: doc.dateLocale } : {}),
    ...(doc.centuryPivot !== undefined ? { centuryPivot: doc.centuryPivot } : {}),
    ...(doc.dateZone ? { dateZone: doc.dateZone } : {}),
    ...(doc.localeSource ? { localeSource: { ...doc.localeSource } } : {}),
    ...(doc.encoding ? { encoding: { ...doc.encoding } } : {}),
    dirty: false,
    loading: false,
    loadError: null,
    saving: false,
  };
  mappingReviewState.panel.open = true;
}

// ---------------------------------------------------------------------------
// Column mutators (per table slice)
// ---------------------------------------------------------------------------

/**
 * Retargets a live column, mirroring the server's "set" semantics for the
 * model resolution: a retarget away from the proposed target drops it (the
 * old target's record links are meaningless on the new field); retargeting
 * back to the proposal restores a fresh copy of it.
 */
export function setColumnTarget(groupId: string, sourceColumn: string, targetField: string): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const column = sl.columns.find((c) => c.sourceColumn === sourceColumn);
  if (!column) return;
  column.targetField = targetField;
  const base = sl.baseColumns.find((c) => c.sourceColumn === sourceColumn);
  if (base) {
    if (targetField === base.targetField && base.resolution) {
      column.resolution = clone(base.resolution);
    } else {
      delete column.resolution;
    }
  }
  markDirty();
}

export function removeColumnMapping(groupId: string, sourceColumn: string): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const index = sl.columns.findIndex((c) => c.sourceColumn === sourceColumn);
  if (index < 0) return;
  sl.columns.splice(index, 1);
  markDirty();
}

export function addColumnMapping(groupId: string, sourceColumn: string, targetField: string): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  if (sl.columns.some((c) => c.sourceColumn === sourceColumn)) return;
  sl.columns.push({ sourceColumn, targetField, confidence: 1, alternatives: [], rules: [] });
  markDirty();
}

/**
 * Restores a column to the model's proposed plan — a deep copy of the
 * `baseColumns` entry — re-adding the mapping if the user removed it. No-op
 * for columns the proposal never mapped.
 */
export function resetColumnToProposal(groupId: string, sourceColumn: string): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const base = sl.baseColumns.find((c) => c.sourceColumn === sourceColumn);
  if (!base) return;
  const restored = clone(base);
  const index = sl.columns.findIndex((c) => c.sourceColumn === sourceColumn);
  if (index >= 0) sl.columns[index] = restored;
  else sl.columns.push(restored);
  markDirty();
}

export function setColumnRules(groupId: string, sourceColumn: string, rules: CellCleaningRule[]): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const column = sl.columns.find((c) => c.sourceColumn === sourceColumn);
  if (!column) return;
  column.rules = rules;
  markDirty();
}

export function setResolutionOverride(
  groupId: string,
  sourceColumn: string,
  normalizedKey: string,
  resolution: TableReferenceValueResolution,
): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const column = sl.columns.find((c) => c.sourceColumn === sourceColumn);
  if (!column) return;
  if (!column.resolution) column.resolution = {};
  column.resolution[normalizedKey] = resolution;
  markDirty();
}

// ---------------------------------------------------------------------------
// Row mutators (per table slice)
// ---------------------------------------------------------------------------

export function toggleRowRejected(groupId: string, rowIndex: number): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  if (sl.rejectedRows[rowIndex]) delete sl.rejectedRows[rowIndex];
  else sl.rejectedRows[rowIndex] = true;
  markDirty();
}

export function rejectRows(groupId: string, rowIndexes: number[]): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  for (const i of rowIndexes) {
    if (sl.rejectedRows[i]) continue;
    sl.rejectedRows[i] = true;
    markDirty();
  }
}

export function restoreRows(groupId: string, rowIndexes: number[]): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  for (const i of rowIndexes) {
    if (!sl.rejectedRows[i]) continue;
    delete sl.rejectedRows[i];
    markDirty();
  }
}

export function setCellOverride(
  groupId: string,
  rowIndex: number,
  sourceColumn: string,
  value: string | number | null,
): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  if (!sl.cellOverrides[rowIndex]) sl.cellOverrides[rowIndex] = {};
  sl.cellOverrides[rowIndex][sourceColumn] = value;
  markDirty();
}

// ---------------------------------------------------------------------------
// Selection mutators (per table slice) — selection is UI state, never
// persisted, so it does not flip `dirty`.
// ---------------------------------------------------------------------------

export function toggleRowSelected(groupId: string, rowIndex: number, shiftKey = false): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const sel = sl.selection;
  if (shiftKey && sel.lastClicked !== null) {
    const lo = Math.min(sel.lastClicked, rowIndex);
    const hi = Math.max(sel.lastClicked, rowIndex);
    for (let i = lo; i <= hi; i += 1) {
      if (sel.selected[i]) continue;
      sel.selected[i] = true;
      sel.count += 1;
    }
  } else if (sel.selected[rowIndex]) {
    delete sel.selected[rowIndex];
    sel.count -= 1;
  } else {
    sel.selected[rowIndex] = true;
    sel.count += 1;
  }
  sel.lastClicked = rowIndex;
}

/** Additively selects every row index matching `predicate`; compose with `clearSelection` for replace semantics. */
export function selectRowsWhere(groupId: string, predicate: (rowIndex: number) => boolean): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  for (let i = 0; i < sl.baseRows.length; i += 1) {
    if (!predicate(i) || sl.selection.selected[i]) continue;
    sl.selection.selected[i] = true;
    sl.selection.count += 1;
  }
}

export function clearSelection(groupId: string): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  sl.selection.selected = {};
  sl.selection.count = 0;
  sl.selection.lastClicked = null;
}

// ---------------------------------------------------------------------------
// Per-table target mutators (document docs) — remove / retarget a whole group.
// A retarget invalidates the model's column plans (they map to the old
// table's fields), so the live columns are cleared to user-decided-only.
// ---------------------------------------------------------------------------

export function setTableTarget(groupId: string, tableId: string): void {
  const s = session();
  const sl = s?.tables[groupId];
  if (!s || !sl) return;
  const retargeted = tableId !== sl.targetTableId;
  sl.targetTableId = tableId;
  sl.targetRemoved = false;
  if (retargeted) sl.columns = [];
  markDirty();
}

export function resetTableTarget(groupId: string): void {
  const s = session();
  const sl = s?.tables[groupId];
  if (!s || !sl || !s.baseDoc || !isWholeDocument(s.baseDoc)) return;
  const plan = s.baseDoc.tables.find((t) => t.ref.id === groupId);
  if (!plan) return;
  sl.targetTableId = plan.tableId;
  sl.targetRemoved = false;
  sl.columns = clone(plan.columns);
  markDirty();
}

export function removeTable(groupId: string): void {
  const s = session();
  const sl = s?.tables[groupId];
  if (!s || !sl) return;
  sl.targetRemoved = true;
  markDirty();
}

export function restoreTable(groupId: string): void {
  const s = session();
  const sl = s?.tables[groupId];
  if (!s || !sl) return;
  sl.targetRemoved = false;
  markDirty();
}

// ---------------------------------------------------------------------------
// Document-level localization control — one number-locale, date-order,
// century-pivot, and timezone offset per staging. Each key in the patch is
// treated independently: a present key with `null` clears that axis back to
// auto, a present key with a value sets it, and an absent key is left
// unchanged. Only the axes the reviewer touched flip their per-axis
// `LocaleProvenance` to `'user'`; the rest keep their staged provenance. The
// resolved number/date/pivot/zone axes fan into every slice so previews re-run.
// Encoding is a load-time fact (a read-only badge), never a user axis here.
// ---------------------------------------------------------------------------

export function setDocumentLocale(patch: {
  locale?: NumberLocale | null;
  dateLocale?: DateLocale | null;
  centuryPivot?: number | null;
  dateZone?: string | null;
}): void {
  const s = session();
  if (!s) return;
  const provenance: LocaleProvenance = { ...s.localeSource };
  if ('locale' in patch) {
    s.locale = patch.locale ?? undefined;
    provenance.number = 'user';
  }
  if ('dateLocale' in patch) {
    s.dateLocale = patch.dateLocale ?? undefined;
    provenance.date = 'user';
  }
  if ('centuryPivot' in patch) {
    s.centuryPivot = patch.centuryPivot ?? undefined;
    provenance.centuryPivot = 'user';
  }
  if ('dateZone' in patch) {
    s.dateZone = patch.dateZone ?? undefined;
    provenance.dateZone = 'user';
  }
  s.localeSource = provenance;
  const slices = [s.import, ...Object.values(s.tables)].filter(
    (sl): sl is MappingReviewTableSlice => sl !== null,
  );
  for (const sl of slices) {
    sl.locale = s.locale;
    sl.dateLocale = s.dateLocale;
    sl.centuryPivot = s.centuryPivot;
    sl.dateZone = s.dateZone;
  }
  markDirty();
}

/** Sets a column's magnitude convention (percentage-target columns); reviewer override. */
export function setColumnPercentScale(
  groupId: string,
  sourceColumn: string,
  percentScale: 'percent' | 'fraction',
): void {
  const sl = tableSliceFor(groupId);
  if (!sl) return;
  const column = sl.columns.find((c) => c.sourceColumn === sourceColumn);
  if (!column) return;
  column.percentScale = percentScale;
  markDirty();
}

// ---------------------------------------------------------------------------
// Field mutators (document docs)
// ---------------------------------------------------------------------------

export function setFieldValue(targetField: string, value: string | number | null): void {
  const s = session();
  const field = s?.fields[targetField];
  if (!s || !field) return;
  field.value = value;
  field.manual = true;
  markDirty();
}

export function setFieldFact(targetField: string, factIndex: number): void {
  const s = session();
  const field = s?.fields[targetField];
  if (!s?.baseDoc || !field || !isWholeDocument(s.baseDoc)) return;
  field.factIndex = factIndex;
  field.value = s.baseDoc.facts[factIndex]?.value ?? null;
  field.manual = true;
  markDirty();
}

export function removeField(targetField: string): void {
  const s = session();
  const field = s?.fields[targetField];
  if (!s || !field) return;
  field.removed = true;
  markDirty();
}

export function restoreField(targetField: string): void {
  const s = session();
  const field = s?.fields[targetField];
  if (!s || !field) return;
  field.removed = false;
  markDirty();
}

/** Adds a field the model missed; user-decided, no fact backing it. */
export function addField(targetField: string, value: string | number | null): void {
  const s = session();
  if (!s || s.fields[targetField]) return;
  s.fields[targetField] = { targetField, factIndex: -1, value, removed: false, manual: true };
  markDirty();
}

// ---------------------------------------------------------------------------
// Reference mutators (document docs)
// ---------------------------------------------------------------------------

export function setReferenceResolution(slotKey: string, resolution: TableReferenceValueResolution): void {
  const s = session();
  const reference = s?.references[slotKey];
  if (!s || !reference) return;
  reference.resolution = resolution;
  reference.manual = true;
  markDirty();
}

export function setReferenceFact(slotKey: string, factIndex: number): void {
  const s = session();
  const reference = s?.references[slotKey];
  if (!s?.baseDoc || !reference || !isWholeDocument(s.baseDoc)) return;
  reference.factIndex = factIndex;
  reference.manual = true;
  markDirty();
}

export function removeReference(slotKey: string): void {
  const s = session();
  const reference = s?.references[slotKey];
  if (!s || !reference) return;
  reference.removed = true;
  markDirty();
}

export function restoreReference(slotKey: string): void {
  const s = session();
  const reference = s?.references[slotKey];
  if (!s || !reference) return;
  reference.removed = false;
  markDirty();
}

/** Adds a reference slot the model missed; user-decided, no fact backing it. */
export function addReference(
  slotKey: string,
  entityId: string,
  resolution: TableReferenceValueResolution,
  entityGroupId?: string,
): void {
  const s = session();
  if (!s || s.references[slotKey]) return;
  s.references[slotKey] = {
    slotKey,
    entityId,
    ...(entityGroupId ? { entityGroupId } : {}),
    factIndex: -1,
    resolution,
    removed: false,
    manual: true,
  };
  markDirty();
}

function markDirty(): void {
  const s = session();
  if (s) s.dirty = true;
}

// ---------------------------------------------------------------------------
// Save-time assembly
// ---------------------------------------------------------------------------

function resolutionOverridesDiff(
  live: ColumnPlan['resolution'],
  base: ColumnPlan['resolution'],
): Record<string, TableReferenceValueResolution> | undefined {
  if (!live) return undefined;
  const changed: Record<string, TableReferenceValueResolution> = {};
  for (const [key, value] of Object.entries(live)) {
    if (JSON.stringify(value) === JSON.stringify(base?.[key])) continue;
    changed[key] = clone(value);
  }
  return Object.keys(changed).length > 0 ? changed : undefined;
}

/** Column-decision diff for one slice: removed/set columns the user changed. */
function columnDecisions(slice: MappingReviewTableSlice): Record<string, ProcessColumnDecision> {
  const liveBySource = new Map(slice.columns.map((c) => [c.sourceColumn, c]));
  const columns: Record<string, ProcessColumnDecision> = {};
  for (const base of slice.baseColumns) {
    const live = liveBySource.get(base.sourceColumn);
    if (!live) {
      columns[base.sourceColumn] = { kind: 'remove' };
      continue;
    }
    const targetChanged = live.targetField !== base.targetField;
    const rulesChanged = JSON.stringify(live.rules) !== JSON.stringify(base.rules);
    const percentScaleChanged = live.percentScale !== base.percentScale;
    const resolutionOverrides = resolutionOverridesDiff(live.resolution, base.resolution);
    if (!targetChanged && !rulesChanged && !percentScaleChanged && !resolutionOverrides) continue;
    columns[base.sourceColumn] = {
      kind: 'set',
      targetField: live.targetField,
      ...(rulesChanged ? { rules: clone(live.rules) } : {}),
      ...(percentScaleChanged && live.percentScale !== undefined ? { percentScale: live.percentScale } : {}),
      ...(resolutionOverrides ? { resolutionOverrides } : {}),
    };
  }
  const baseSources = new Set(slice.baseColumns.map((c) => c.sourceColumn));
  for (const live of slice.columns) {
    if (baseSources.has(live.sourceColumn)) continue;
    const resolutionOverrides = resolutionOverridesDiff(live.resolution, undefined);
    columns[live.sourceColumn] = {
      kind: 'set',
      targetField: live.targetField,
      ...(live.rules.length > 0 ? { rules: clone(live.rules) } : {}),
      ...(live.percentScale !== undefined ? { percentScale: live.percentScale } : {}),
      ...(resolutionOverrides ? { resolutionOverrides } : {}),
    };
  }
  return columns;
}

/** Row-decision diffs for one slice, clamped to the classified coverage. */
function rowDecisions(slice: MappingReviewTableSlice): {
  rejectedRows: number[];
  cellOverrides: Record<number, Record<string, string | number | null>>;
} {
  const rowLimit = slice.baseRows.length;
  const rejectedRows = Object.keys(slice.rejectedRows)
    .map(Number)
    .filter((i) => i < rowLimit)
    .sort((a, b) => a - b);
  const cellOverrides = clone(slice.cellOverrides);
  for (const key of Object.keys(cellOverrides)) {
    if (Number(key) >= rowLimit) delete cellOverrides[Number(key)];
  }
  return { rejectedRows, cellOverrides };
}

/** Per-slice reference resolved/unresolved counts over non-rejected, in-cap rows. */
function referenceCounts(slice: MappingReviewTableSlice): { resolved: number; unresolved: number } {
  const rejected = new Set(Object.keys(slice.rejectedRows).map(Number));
  const counts = { resolved: 0, unresolved: 0 };
  for (const column of slice.columns.filter((c) => c.resolution)) {
    const keys = referenceKeyOccurrences({
      chunks: slice.chunks,
      sourceColumn: column.sourceColumn,
      cellOverrides: slice.cellOverrides,
      rejected,
      rowLimit: slice.baseRows.length,
    });
    for (const key of keys.keys()) {
      if (column.resolution?.[key]?.kind === 'entity') counts.resolved += 1;
      else counts.unresolved += 1;
    }
  }
  return counts;
}

/** Per-table summary recompute, mirroring the server's `TableMappingSummary`. */
function tableSummary(slice: MappingReviewTableSlice): TableMappingSummary {
  const sourceColumns = [...new Set(slice.chunks.flatMap((c) => c.sourceColumns))];
  const mappedSet = new Set(slice.columns.map((c) => c.sourceColumn));
  const unmappedSourceColumns = sourceColumns.filter((c) => !mappedSet.has(c));
  const tagCounts = slice.baseRows.reduce<TableMappingSummary['tagCounts']>(
    (acc, r) => ({ ...acc, [r.tag]: acc[r.tag] + 1 }),
    { value: 0, summary: 0, junk: 0 },
  );
  const columnDiff = columnDecisions(slice);
  const lowConfidence = slice.columns.filter(
    (c) => !(c.sourceColumn in columnDiff) && c.confidence < CONFIDENCE_WARN_THRESHOLD,
  ).length;
  const referenceColumns = slice.columns.filter((c) => c.resolution);
  const counts = referenceCounts(slice);
  return {
    mapped: slice.columns.length,
    referenceColumns: referenceColumns.length,
    referenceValuesResolved: counts.resolved,
    referenceValuesUnresolved: counts.unresolved,
    unmappedSourceColumns,
    rowsTotal: slice.baseRows.length,
    tagCounts,
    lowConfidence,
  };
}

/** Field-decision diff (document docs). */
function fieldDecisions(s: MappingReviewSession, doc: ProcessWholeDocument): Record<string, ProcessFieldDecision> {
  const planByField = new Map(doc.fields.map((p) => [p.targetField, p]));
  const decisions: Record<string, ProcessFieldDecision> = {};
  for (const field of Object.values(s.fields)) {
    if (field.removed) {
      if (planByField.has(field.targetField)) decisions[field.targetField] = { kind: 'remove' };
      continue;
    }
    const plan = planByField.get(field.targetField);
    if (!plan) {
      decisions[field.targetField] = { kind: 'set', value: field.value };
      continue;
    }
    const proposed = doc.facts[plan.factIndex]?.value ?? null;
    const factChanged = field.factIndex !== plan.factIndex;
    if (field.manual && (factChanged || field.value !== proposed)) {
      decisions[field.targetField] = { kind: 'set', value: field.value };
    }
  }
  return decisions;
}

/** Reference-decision diff (document docs). */
function referenceDecisions(
  s: MappingReviewSession,
  doc: ProcessWholeDocument,
): Record<string, ProcessReferenceDecision> {
  const planBySlot = new Map(doc.references.map((p) => [p.slotKey, p]));
  const decisions: Record<string, ProcessReferenceDecision> = {};
  for (const reference of Object.values(s.references)) {
    if (reference.removed) {
      if (planBySlot.has(reference.slotKey)) decisions[reference.slotKey] = { kind: 'remove' };
      continue;
    }
    const set = (): ProcessReferenceDecision => ({
      kind: 'set',
      resolution: clone(reference.resolution),
      entityId: reference.entityId,
      ...(reference.entityGroupId ? { entityGroupId: reference.entityGroupId } : {}),
    });
    const plan = planBySlot.get(reference.slotKey);
    if (!plan) {
      decisions[reference.slotKey] = set();
      continue;
    }
    if (reference.manual && JSON.stringify(reference.resolution) !== JSON.stringify(plan.resolution)) {
      decisions[reference.slotKey] = set();
    }
  }
  return decisions;
}

/** Per-table decisions diff (document docs): column/row edits + target retarget/remove. */
function tableDecisions(
  s: MappingReviewSession,
  doc: ProcessWholeDocument,
): Record<string, ProcessTableDecisions> {
  const planByGroup = new Map(doc.tables.map((p) => [p.ref.id, p]));
  const decisions: Record<string, ProcessTableDecisions> = {};
  for (const [groupId, slice] of Object.entries(s.tables)) {
    const plan = planByGroup.get(groupId);
    const { rejectedRows, cellOverrides } = rowDecisions(slice);
    const columns = columnDecisions(slice);
    const targetDecision = slice.targetRemoved
      ? { kind: 'remove' as const }
      : plan && slice.targetTableId !== plan.tableId
        ? { kind: 'set' as const, tableId: slice.targetTableId }
        : undefined;
    const hasRowEdits = rejectedRows.length > 0 || Object.keys(cellOverrides).length > 0;
    const hasColumnEdits = Object.keys(columns).length > 0;
    if (!targetDecision && !hasRowEdits && !hasColumnEdits) continue;
    decisions[groupId] = {
      rejectedRows,
      columns,
      cellOverrides,
      ...(targetDecision ? { target: targetDecision } : {}),
    };
  }
  return decisions;
}

function documentSummary(s: MappingReviewSession, doc: ProcessWholeDocument): DocumentMappingSummary {
  const keptFields = Object.values(s.fields).filter((f) => !f.removed);
  const fieldsLowConfidence = doc.fields.filter((p) => {
    const live = s.fields[p.targetField];
    return live && !live.removed && !live.manual && p.confidence < CONFIDENCE_WARN_THRESHOLD;
  }).length;
  const keptReferences = Object.values(s.references).filter((r) => !r.removed);
  const referencesResolved = keptReferences.filter((r) => r.resolution.kind === 'entity').length;
  const unassignedTableGroups: string[] = [];
  let tablesAssigned = 0;
  for (const groupId of s.tableOrder) {
    if (s.tables[groupId]?.targetRemoved) unassignedTableGroups.push(groupId);
    else tablesAssigned += 1;
  }
  return {
    factsExtracted: doc.facts.length,
    fieldsMapped: keptFields.length,
    fieldsLowConfidence,
    referencesMapped: keptReferences.length,
    referencesResolved,
    referencesUnresolved: keptReferences.length - referencesResolved,
    tablesAssigned,
    unassignedTableGroups,
  };
}

/**
 * Pure: assembles the document to persist — the untouched base proposal plus
 * a `decisions` overlay derived by diffing the live edited state against
 * `baseDoc`, plus a recomputed `summary`. Never mutates its inputs. Row
 * decisions are clamped to the classified coverage (`baseRows.length`, the
 * server's import cap). Dispatches on `target.kind`: an `entityRecords`
 * import emits `ProcessImportDecisions`, a whole `document` emits
 * `ProcessDocumentDecisions`.
 */
export function buildEditedProcessDocument(s: MappingReviewSession): ProcessDocument {
  const { baseDoc } = s;
  if (!baseDoc || (isWholeDocument(baseDoc) ? false : !s.import)) {
    throw new Error('Mapping review source not loaded; cannot build the edited process document');
  }

  if (!isWholeDocument(baseDoc)) {
    const slice = s.import!;
    const { rejectedRows, cellOverrides } = rowDecisions(slice);
    const decisions: ProcessImportDecisions = {
      version: 1,
      rejectedRows,
      columns: columnDecisions(slice),
      cellOverrides,
    };
    const summary = tableSummary(slice);
    // Session locales are authoritative: clearing an axis back to Auto must override
    // the staged value, so drop baseDoc's locale fields before re-applying the session's.
    // `encoding` is a load-time fact, not a user axis — it stays in `base` untouched.
    const { locale, dateLocale, centuryPivot, dateZone, localeSource, ...base } = baseDoc;
    const imported: ProcessImportDocument = {
      ...base,
      decisions,
      summary,
      ...(s.locale ? { locale: s.locale } : {}),
      ...(s.dateLocale ? { dateLocale: s.dateLocale } : {}),
      ...(s.centuryPivot !== undefined ? { centuryPivot: s.centuryPivot } : {}),
      ...(s.dateZone ? { dateZone: s.dateZone } : {}),
      ...(s.localeSource ? { localeSource: s.localeSource } : {}),
    };
    return imported;
  }

  const decisions: ProcessDocumentDecisions = {
    version: 1,
    fields: fieldDecisions(s, baseDoc),
    references: referenceDecisions(s, baseDoc),
    tables: tableDecisions(s, baseDoc),
  };
  const summary = documentSummary(s, baseDoc);
  // Session locales are authoritative (see the import branch above); `encoding`
  // is a load-time fact and stays in `base` untouched.
  const { locale, dateLocale, centuryPivot, dateZone, localeSource, ...base } = baseDoc;
  const document: ProcessWholeDocument = {
    ...base,
    decisions,
    summary,
    ...(s.locale ? { locale: s.locale } : {}),
    ...(s.dateLocale ? { dateLocale: s.dateLocale } : {}),
    ...(s.centuryPivot !== undefined ? { centuryPivot: s.centuryPivot } : {}),
    ...(s.dateZone ? { dateZone: s.dateZone } : {}),
    ...(s.localeSource ? { localeSource: s.localeSource } : {}),
  };
  return document;
}

export { tableSummary, referenceCounts, isWholeDocument };
export type { MappingReviewTableSlice };
